// /src/com/file_create_his/FileCreateHisArrivalOnTimeReport.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { Button, CircularProgress } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import DateRangePicker from '../../common/DateRangePicker';
import {ContentConainer, ContentHeader, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle } from '../../value/AppConst';
import { authTableTreeRepo, fchArrivalOnTimeReportListRepo, userInfoRepo } from '../../value/AppModel';
import {
    LIST_FILE_CREATE_HIS_ARRIVAL_ON_TIME_REPORT, REMOVE_FILE_CREATE_HIS
} from './FileCreateHisGql';
import { FileCreateHisDictionary } from './FileCreateHisDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import ValueMisc from '../../value/ValueMisc';
import { ArrivalOnTimeReportExcelColumns, ArrivalOnTimeReportMeta } from '../file_of_report/ArrivalOnTimeReportDef';
import FileCreateHisDownload from './FileCreateHisDownload';
import ArrivalOnTimeReportInput from '../file_of_report/ArrivalOnTimeReportInput';

const DaysToLoad = 7; // Default duration to load


/**
 * (FILE_CREATE_HIS) 정보 View Controller - 정시도착 리포트
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-11 14:19:23 using DbdbTap
 */
export default function FileCreateHisArrivalOnTimeReport({
    forTruckCom = false
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    //const dcInfoListData = useReactiveVar(dcInfoListRepo); // for selection.
    const ReportMeta = useMemo(() => {
        return sessionInfo.isTransCompanyUser() ? ArrivalOnTimeReportMeta.transporter : ArrivalOnTimeReportMeta.center;
    }, [sessionInfo]);
    const [duration, setDuration] = useState({dateFrom:'', dateTill:''});
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const fileCreateHisListData = useReactiveVar(fchArrivalOnTimeReportListRepo);
    const [inputDialogState, setInputDialogState] = useState({open:false, forEventReport:false});
    const [downloaderState, setDownloaderState] = useState({open:false, item:null, instructions:null, columns:[]});
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const openCountRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getFileCreateHisList, responseList] = useLazyQuery(LIST_FILE_CREATE_HIS_ARRIVAL_ON_TIME_REPORT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: FileCreateHisDictionary.errorArrivalOnTimeTitle.Get, message: [error.message]})
    });
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [removeFileCreateHis, responseRemove] = useMutation( REMOVE_FILE_CREATE_HIS, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: FileCreateHisDictionary.errorArrivalOnTimeTitle.Remove, message: [error.message]})
	});

    const getFileCreateHisListNow = (dateFrom, dateTill) => {
        const param = {variables:{dateFrom:dateFrom, dateTill:dateTill}};
        getFileCreateHisList(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        // set 3 days for first time loading
        if(openCountRef.current === 0) {
            openCountRef.current = 1;
            const dateTill = ValueMisc.getYMD();
            const dateFrom = ValueMisc.getYMD(ValueMisc.getDateAgo(DaysToLoad));
            setDuration({dateFrom:dateFrom, dateTill:dateTill});
            getFileCreateHisListNow(dateFrom, dateTill);
        }
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.fileCreateHisArrivalOnTimeReportList) {
            fchArrivalOnTimeReportListRepo({when:new Date().getTime()/1000, records:data.fileCreateHisArrivalOnTimeReportList});
        }
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.fileCreateHisRemove.ok) getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        else
            addAlert({title:FileCreateHisDictionary.errorArrivalOnTimeTitle.Remove, message:[data.fileCreateHisRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeFrom = (ymd) => {
        setDuration({...duration, dateFrom:ymd});
        lastReloadRef.current = 0;
    };

    const onChangeTo = (ymd) => {
        setDuration({...duration, dateTill:ymd});
        lastReloadRef.current = 0;
    };

    const onClickLoad = () => {
        const diff = Math.round(new Date().getTime()/1000 - lastReloadRef.current);
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(`동일한 조건으로는 1분 내에 검색할 수 없습니다. ${60-diff}초 후에 다시 시도해 주세요.`, {variant:'info', autoHideDuration:2000});
        }
        else {
            getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        }
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "정시도착 리포트 삭제",
            content: [
                `정시도착 리포트를 삭제합니다.`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeFileCreateHis({variables:{fileCreateHis:{fileCreateHisSeq:item.fileCreateHisSeq, serverUrl: item.serverUrl}}});
    };

    const onClickAdd = (forEventReport) => {
        setInputDialogState({open:true, forEventReport:forEventReport});
    };

    const onCompleteReportCreation = () => {
        getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        setInputDialogState({open:false, forEventReport:false});
    };

    const openDownloaderForItem = (item) => {
        const instructions = ReportMeta.instructions;
        const fileFlag = item.fileFlag;
        let columns;
        if(fileFlag === 'COM_DOT_RPT') columns = ArrivalOnTimeReportExcelColumns.transporter.columns;
        else if(fileFlag === 'COM_DOT_EVT_RPT') columns = ArrivalOnTimeReportExcelColumns.transporter.eventColumns;
        else if(fileFlag === 'DOT_EVT_RPT') columns = ArrivalOnTimeReportExcelColumns.center.eventColumns;
        else columns = ArrivalOnTimeReportExcelColumns.center.columns;
        
        setDownloaderState({
            open:true, item:item, instructions:instructions,
            columns: columns
        });
    }

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DOWNLOAD: openDownloaderForItem(row); break;
            case AppObject.command.DELETE: confirmRemove(row); break;
            default: return;
        }
    };

    const canRemove = forTruckCom || checkAuth(authTableTree.tree.pr_file_create_his_remove);
    const tools = [
        {id:AppObject.command.DOWNLOAD, icon:<SimCardDownloadIcon/>, color: AppStyle.mui.primary},
        // {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
    ];
    if(canRemove) {
        tools.push(
            {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning}
        );
    }

    const loadingAny = responseList.loading || responseRemove.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <DateRangePicker
                        dateFrom={duration.dateFrom}
                        dateTo={duration.dateTill}
                        onChangeFrom={onChangeFrom}
                        onChangeTo={onChangeTo}
                    />
                    {
                        responseRemove.loading ||
                        responseList.loading ?
                        <CircularProgress size={20} style={{marginLeft:10}}/>
                        :
                        <Button
                            startIcon={<ListAltIcon />}
                            onClick={onClickLoad}
                            variant={AppStyle.mui.contained}
                            style={{marginLeft:10}}
                        >
                            조회
                        </Button>
                    }
                    {/* <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    /> */}
                </ControlBox>
                <ToolBox>
                    <Button
                        startIcon={<NoteAddIcon />}
                        disabled={loadingAny}
                        variant={AppStyle.mui.contained}
                        style={{marginLeft:0}}
                        onClick={() => onClickAdd(false)}
                    >
                        정시도착 리포트 생성
                    </Button>
                    {
                        !sessionInfo.isTransCompanyUser() &&
                        <Button
                            startIcon={<AddLocationIcon />}
                            disabled={loadingAny}
                            variant={AppStyle.mui.contained}
                            style={{marginLeft:10}}
                            onClick={() => onClickAdd(true)}
                        >
                            정시도착 GPS 이벤트 리포트 생성
                        </Button>}
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={FileCreateHisDictionary.tableProps.tableId}
                tableProps={FileCreateHisDictionary.tableProps}
                records={fileCreateHisListData.records}
                tools={tools}
                onClickTool={onClickToolOnRow}
            />
            <ArrivalOnTimeReportInput
                open={inputDialogState.open}
                forEventReport={inputDialogState.forEventReport}
                onClose={() => setInputDialogState({open:false, forEventReport:false})}
                onComplete={onCompleteReportCreation}
            />
            <FileCreateHisDownload
                open={downloaderState.open}
                item={downloaderState.item}
                columns={downloaderState.columns}
                instructions={downloaderState.instructions}
                getExtraHeaders={ReportMeta.getExtraHeaders}
                onClose={() => setDownloaderState({open:false, item:null, instructions:null, columns:[]})}
            />
        </ContentConainer>
    );
}
